/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  CancelQueryPathParameters,
  ExportFoundryQueryResultPathParameters,
  GetCatalogParams,
  GetCatalogTablesParams,
  GetChunkDataParams,
  GetFoundryQueryExportStatusPathParameters,
  GetQueryBatchStatusPathParameters,
  GetQueryResultDataParams,
  GetQueryResultDataPathParameters,
  GetQueryStatusParams,
  GetQueryStatusPathParameters,
  TLExecuteQueryBatchRequest,
  TLExecuteQueryRequest,
  TLExportQueryResultRequest,
  TLFoundryConfigCatalogResponse,
  TLFoundryExportStatusResponse,
  TLHTTPValidationError,
  TLQueryBatchStatusResponse,
  TLQueryResultDataResponse,
  TLQueryStatusResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Execute Query
 */
export const executeQuery = (
  tLExecuteQueryRequest: BodyType<TLExecuteQueryRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLQueryStatusResponse>(
    {
      url: `/foundry/query`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLExecuteQueryRequest,
      signal,
    },
    options,
  );
};

export const getExecuteQueryQueryKey = (tLExecuteQueryRequest?: BodyType<TLExecuteQueryRequest>) => {
  return ['POST', 'foundry', 'query', tLExecuteQueryRequest] as const;
};

export const getExecuteQueryQueryOptions = <
  TData = Awaited<ReturnType<typeof executeQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExecuteQueryRequest: BodyType<TLExecuteQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getExecuteQueryQueryKey(tLExecuteQueryRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof executeQuery>>> = ({ signal }) =>
    executeQuery(tLExecuteQueryRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof executeQuery>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ExecuteQueryQueryResult = NonNullable<Awaited<ReturnType<typeof executeQuery>>>;
export type ExecuteQueryQueryError = ErrorType<TLHTTPValidationError>;

export function useExecuteQuery<
  TData = Awaited<ReturnType<typeof executeQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExecuteQueryRequest: BodyType<TLExecuteQueryRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQuery>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeQuery>>,
          TError,
          Awaited<ReturnType<typeof executeQuery>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteQuery<
  TData = Awaited<ReturnType<typeof executeQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExecuteQueryRequest: BodyType<TLExecuteQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQuery>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeQuery>>,
          TError,
          Awaited<ReturnType<typeof executeQuery>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteQuery<
  TData = Awaited<ReturnType<typeof executeQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExecuteQueryRequest: BodyType<TLExecuteQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Execute Query
 */

export function useExecuteQuery<
  TData = Awaited<ReturnType<typeof executeQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExecuteQueryRequest: BodyType<TLExecuteQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExecuteQueryQueryOptions(tLExecuteQueryRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Execute Query
 */
export const prefetchExecuteQueryQuery = async <
  TData = Awaited<ReturnType<typeof executeQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLExecuteQueryRequest: BodyType<TLExecuteQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExecuteQueryQueryOptions(tLExecuteQueryRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Execute Query
 */
export const invalidateExecuteQuery = async (
  queryClient: QueryClient,
  tLExecuteQueryRequest: BodyType<TLExecuteQueryRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getExecuteQueryQueryKey(tLExecuteQueryRequest) }, options);

  return queryClient;
};

/**
 * @summary Execute Query Batch
 */
export const executeQueryBatch = (
  tLExecuteQueryBatchRequest: BodyType<TLExecuteQueryBatchRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLQueryBatchStatusResponse>(
    {
      url: `/foundry/query/batch`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLExecuteQueryBatchRequest,
      signal,
    },
    options,
  );
};

export const getExecuteQueryBatchQueryKey = (tLExecuteQueryBatchRequest?: BodyType<TLExecuteQueryBatchRequest>) => {
  return ['POST', 'foundry', 'query', 'batch', tLExecuteQueryBatchRequest] as const;
};

export const getExecuteQueryBatchQueryOptions = <
  TData = Awaited<ReturnType<typeof executeQueryBatch>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExecuteQueryBatchRequest: BodyType<TLExecuteQueryBatchRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQueryBatch>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getExecuteQueryBatchQueryKey(tLExecuteQueryBatchRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof executeQueryBatch>>> = ({ signal }) =>
    executeQueryBatch(tLExecuteQueryBatchRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof executeQueryBatch>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ExecuteQueryBatchQueryResult = NonNullable<Awaited<ReturnType<typeof executeQueryBatch>>>;
export type ExecuteQueryBatchQueryError = ErrorType<TLHTTPValidationError>;

export function useExecuteQueryBatch<
  TData = Awaited<ReturnType<typeof executeQueryBatch>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExecuteQueryBatchRequest: BodyType<TLExecuteQueryBatchRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQueryBatch>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeQueryBatch>>,
          TError,
          Awaited<ReturnType<typeof executeQueryBatch>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteQueryBatch<
  TData = Awaited<ReturnType<typeof executeQueryBatch>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExecuteQueryBatchRequest: BodyType<TLExecuteQueryBatchRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQueryBatch>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof executeQueryBatch>>,
          TError,
          Awaited<ReturnType<typeof executeQueryBatch>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExecuteQueryBatch<
  TData = Awaited<ReturnType<typeof executeQueryBatch>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExecuteQueryBatchRequest: BodyType<TLExecuteQueryBatchRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQueryBatch>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Execute Query Batch
 */

export function useExecuteQueryBatch<
  TData = Awaited<ReturnType<typeof executeQueryBatch>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLExecuteQueryBatchRequest: BodyType<TLExecuteQueryBatchRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQueryBatch>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExecuteQueryBatchQueryOptions(tLExecuteQueryBatchRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Execute Query Batch
 */
export const prefetchExecuteQueryBatchQuery = async <
  TData = Awaited<ReturnType<typeof executeQueryBatch>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLExecuteQueryBatchRequest: BodyType<TLExecuteQueryBatchRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof executeQueryBatch>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExecuteQueryBatchQueryOptions(tLExecuteQueryBatchRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Execute Query Batch
 */
export const invalidateExecuteQueryBatch = async (
  queryClient: QueryClient,
  tLExecuteQueryBatchRequest: BodyType<TLExecuteQueryBatchRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getExecuteQueryBatchQueryKey(tLExecuteQueryBatchRequest) }, options);

  return queryClient;
};

/**
 * @summary Get Query Batch Status
 */
export const getQueryBatchStatus = (
  { jobId }: GetQueryBatchStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLQueryBatchStatusResponse>(
    { url: `/foundry/query/batch/${jobId}`, method: 'GET', signal },
    options,
  );
};

export const getGetQueryBatchStatusQueryKey = ({ jobId }: GetQueryBatchStatusPathParameters) => {
  return ['foundry', 'query', 'batch', jobId] as const;
};

export const getGetQueryBatchStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getQueryBatchStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetQueryBatchStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryBatchStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetQueryBatchStatusQueryKey({ jobId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getQueryBatchStatus>>> = ({ signal }) =>
    getQueryBatchStatus({ jobId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getQueryBatchStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetQueryBatchStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getQueryBatchStatus>>>;
export type GetQueryBatchStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useGetQueryBatchStatus<
  TData = Awaited<ReturnType<typeof getQueryBatchStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetQueryBatchStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryBatchStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getQueryBatchStatus>>,
          TError,
          Awaited<ReturnType<typeof getQueryBatchStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetQueryBatchStatus<
  TData = Awaited<ReturnType<typeof getQueryBatchStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetQueryBatchStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryBatchStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getQueryBatchStatus>>,
          TError,
          Awaited<ReturnType<typeof getQueryBatchStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetQueryBatchStatus<
  TData = Awaited<ReturnType<typeof getQueryBatchStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetQueryBatchStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryBatchStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Query Batch Status
 */

export function useGetQueryBatchStatus<
  TData = Awaited<ReturnType<typeof getQueryBatchStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetQueryBatchStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryBatchStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetQueryBatchStatusQueryOptions({ jobId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Query Batch Status
 */
export const prefetchGetQueryBatchStatusQuery = async <
  TData = Awaited<ReturnType<typeof getQueryBatchStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { jobId }: GetQueryBatchStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryBatchStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetQueryBatchStatusQueryOptions({ jobId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Query Batch Status
 */
export const invalidateGetQueryBatchStatus = async (
  queryClient: QueryClient,
  { jobId }: GetQueryBatchStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetQueryBatchStatusQueryKey({ jobId }) }, options);

  return queryClient;
};

/**
 * @summary Get Catalog
 */
export const getCatalog = (
  params?: GetCatalogParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLQueryStatusResponse>({ url: `/foundry/query/catalog`, method: 'POST', params, signal }, options);
};

export const getGetCatalogQueryKey = (params?: GetCatalogParams) => {
  return ['POST', 'foundry', 'query', 'catalog', ...(params ? [params] : [])] as const;
};

export const getGetCatalogQueryOptions = <
  TData = Awaited<ReturnType<typeof getCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCatalogQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCatalog>>> = ({ signal }) =>
    getCatalog(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCatalog>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCatalogQueryResult = NonNullable<Awaited<ReturnType<typeof getCatalog>>>;
export type GetCatalogQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCatalog<
  TData = Awaited<ReturnType<typeof getCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetCatalogParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalog>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCatalog>>,
          TError,
          Awaited<ReturnType<typeof getCatalog>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCatalog<
  TData = Awaited<ReturnType<typeof getCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalog>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCatalog>>,
          TError,
          Awaited<ReturnType<typeof getCatalog>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCatalog<
  TData = Awaited<ReturnType<typeof getCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Catalog
 */

export function useGetCatalog<
  TData = Awaited<ReturnType<typeof getCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCatalogQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Catalog
 */
export const prefetchGetCatalogQuery = async <
  TData = Awaited<ReturnType<typeof getCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetCatalogParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCatalogQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Catalog
 */
export const invalidateGetCatalog = async (
  queryClient: QueryClient,
  params?: GetCatalogParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetCatalogQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Catalog Tables
 */
export const getCatalogTables = (
  params?: GetCatalogTablesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFoundryConfigCatalogResponse>(
    { url: `/foundry/query/catalog/tables`, method: 'GET', params, signal },
    options,
  );
};

export const getGetCatalogTablesQueryKey = (params?: GetCatalogTablesParams) => {
  return ['foundry', 'query', 'catalog', 'tables', ...(params ? [params] : [])] as const;
};

export const getGetCatalogTablesQueryOptions = <
  TData = Awaited<ReturnType<typeof getCatalogTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogTablesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCatalogTablesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCatalogTables>>> = ({ signal }) =>
    getCatalogTables(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCatalogTables>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCatalogTablesQueryResult = NonNullable<Awaited<ReturnType<typeof getCatalogTables>>>;
export type GetCatalogTablesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetCatalogTables<
  TData = Awaited<ReturnType<typeof getCatalogTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetCatalogTablesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTables>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCatalogTables>>,
          TError,
          Awaited<ReturnType<typeof getCatalogTables>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCatalogTables<
  TData = Awaited<ReturnType<typeof getCatalogTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogTablesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTables>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCatalogTables>>,
          TError,
          Awaited<ReturnType<typeof getCatalogTables>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCatalogTables<
  TData = Awaited<ReturnType<typeof getCatalogTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogTablesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Catalog Tables
 */

export function useGetCatalogTables<
  TData = Awaited<ReturnType<typeof getCatalogTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetCatalogTablesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCatalogTablesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Catalog Tables
 */
export const prefetchGetCatalogTablesQuery = async <
  TData = Awaited<ReturnType<typeof getCatalogTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetCatalogTablesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCatalogTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetCatalogTablesQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Catalog Tables
 */
export const invalidateGetCatalogTables = async (
  queryClient: QueryClient,
  params?: GetCatalogTablesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetCatalogTablesQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Chunk Data
 */
export const getChunkData = (
  params: GetChunkDataParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLQueryResultDataResponse>(
    { url: `/foundry/query/chunk`, method: 'GET', params, signal },
    options,
  );
};

export const getGetChunkDataQueryKey = (params?: GetChunkDataParams) => {
  return ['foundry', 'query', 'chunk', ...(params ? [params] : [])] as const;
};

export const getGetChunkDataQueryOptions = <
  TData = Awaited<ReturnType<typeof getChunkData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetChunkDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChunkData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetChunkDataQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getChunkData>>> = ({ signal }) =>
    getChunkData(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getChunkData>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetChunkDataQueryResult = NonNullable<Awaited<ReturnType<typeof getChunkData>>>;
export type GetChunkDataQueryError = ErrorType<TLHTTPValidationError>;

export function useGetChunkData<
  TData = Awaited<ReturnType<typeof getChunkData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetChunkDataParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChunkData>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getChunkData>>,
          TError,
          Awaited<ReturnType<typeof getChunkData>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetChunkData<
  TData = Awaited<ReturnType<typeof getChunkData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetChunkDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChunkData>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getChunkData>>,
          TError,
          Awaited<ReturnType<typeof getChunkData>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetChunkData<
  TData = Awaited<ReturnType<typeof getChunkData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetChunkDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChunkData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Chunk Data
 */

export function useGetChunkData<
  TData = Awaited<ReturnType<typeof getChunkData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetChunkDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChunkData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetChunkDataQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Chunk Data
 */
export const prefetchGetChunkDataQuery = async <
  TData = Awaited<ReturnType<typeof getChunkData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: GetChunkDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChunkData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetChunkDataQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Chunk Data
 */
export const invalidateGetChunkData = async (
  queryClient: QueryClient,
  params: GetChunkDataParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetChunkDataQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Foundry Query Export Status
 */
export const getFoundryQueryExportStatus = (
  { exportJobId }: GetFoundryQueryExportStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFoundryExportStatusResponse>(
    { url: `/foundry/query/exports/${exportJobId}`, method: 'GET', signal },
    options,
  );
};

export const getGetFoundryQueryExportStatusQueryKey = ({ exportJobId }: GetFoundryQueryExportStatusPathParameters) => {
  return ['foundry', 'query', 'exports', exportJobId] as const;
};

export const getGetFoundryQueryExportStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getFoundryQueryExportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { exportJobId }: GetFoundryQueryExportStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFoundryQueryExportStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFoundryQueryExportStatusQueryKey({ exportJobId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFoundryQueryExportStatus>>> = ({ signal }) =>
    getFoundryQueryExportStatus({ exportJobId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: exportJobId !== null && exportJobId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getFoundryQueryExportStatus>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetFoundryQueryExportStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFoundryQueryExportStatus>>
>;
export type GetFoundryQueryExportStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useGetFoundryQueryExportStatus<
  TData = Awaited<ReturnType<typeof getFoundryQueryExportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFoundryQueryExportStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFoundryQueryExportStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFoundryQueryExportStatus>>,
          TError,
          Awaited<ReturnType<typeof getFoundryQueryExportStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFoundryQueryExportStatus<
  TData = Awaited<ReturnType<typeof getFoundryQueryExportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFoundryQueryExportStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFoundryQueryExportStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFoundryQueryExportStatus>>,
          TError,
          Awaited<ReturnType<typeof getFoundryQueryExportStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFoundryQueryExportStatus<
  TData = Awaited<ReturnType<typeof getFoundryQueryExportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFoundryQueryExportStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFoundryQueryExportStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Foundry Query Export Status
 */

export function useGetFoundryQueryExportStatus<
  TData = Awaited<ReturnType<typeof getFoundryQueryExportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { exportJobId }: GetFoundryQueryExportStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFoundryQueryExportStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetFoundryQueryExportStatusQueryOptions({ exportJobId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Foundry Query Export Status
 */
export const prefetchGetFoundryQueryExportStatusQuery = async <
  TData = Awaited<ReturnType<typeof getFoundryQueryExportStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { exportJobId }: GetFoundryQueryExportStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFoundryQueryExportStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetFoundryQueryExportStatusQueryOptions({ exportJobId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Foundry Query Export Status
 */
export const invalidateGetFoundryQueryExportStatus = async (
  queryClient: QueryClient,
  { exportJobId }: GetFoundryQueryExportStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetFoundryQueryExportStatusQueryKey({ exportJobId }) }, options);

  return queryClient;
};

/**
 * @summary Get Query Status
 */
export const getQueryStatus = (
  { jobId }: GetQueryStatusPathParameters,
  params?: GetQueryStatusParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLQueryStatusResponse>({ url: `/foundry/query/${jobId}`, method: 'GET', params, signal }, options);
};

export const getGetQueryStatusQueryKey = ({ jobId }: GetQueryStatusPathParameters, params?: GetQueryStatusParams) => {
  return ['foundry', 'query', jobId, ...(params ? [params] : [])] as const;
};

export const getGetQueryStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetQueryStatusPathParameters,
  params?: GetQueryStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetQueryStatusQueryKey({ jobId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getQueryStatus>>> = ({ signal }) =>
    getQueryStatus({ jobId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getQueryStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetQueryStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getQueryStatus>>>;
export type GetQueryStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useGetQueryStatus<
  TData = Awaited<ReturnType<typeof getQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetQueryStatusPathParameters,
  params: undefined | GetQueryStatusParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getQueryStatus>>,
          TError,
          Awaited<ReturnType<typeof getQueryStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetQueryStatus<
  TData = Awaited<ReturnType<typeof getQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetQueryStatusPathParameters,
  params?: GetQueryStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getQueryStatus>>,
          TError,
          Awaited<ReturnType<typeof getQueryStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetQueryStatus<
  TData = Awaited<ReturnType<typeof getQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetQueryStatusPathParameters,
  params?: GetQueryStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Query Status
 */

export function useGetQueryStatus<
  TData = Awaited<ReturnType<typeof getQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetQueryStatusPathParameters,
  params?: GetQueryStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetQueryStatusQueryOptions({ jobId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Query Status
 */
export const prefetchGetQueryStatusQuery = async <
  TData = Awaited<ReturnType<typeof getQueryStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { jobId }: GetQueryStatusPathParameters,
  params?: GetQueryStatusParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetQueryStatusQueryOptions({ jobId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Query Status
 */
export const invalidateGetQueryStatus = async (
  queryClient: QueryClient,
  { jobId }: GetQueryStatusPathParameters,
  params?: GetQueryStatusParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetQueryStatusQueryKey({ jobId }, params) }, options);

  return queryClient;
};

/**
 * @summary Cancel Query
 */
export const cancelQuery = (
  { jobId }: CancelQueryPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLQueryStatusResponse>({ url: `/foundry/query/${jobId}/cancel`, method: 'POST', signal }, options);
};

export const getCancelQueryQueryKey = ({ jobId }: CancelQueryPathParameters) => {
  return ['POST', 'foundry', 'query', jobId, 'cancel'] as const;
};

export const getCancelQueryQueryOptions = <
  TData = Awaited<ReturnType<typeof cancelQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: CancelQueryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCancelQueryQueryKey({ jobId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof cancelQuery>>> = ({ signal }) =>
    cancelQuery({ jobId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof cancelQuery>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CancelQueryQueryResult = NonNullable<Awaited<ReturnType<typeof cancelQuery>>>;
export type CancelQueryQueryError = ErrorType<TLHTTPValidationError>;

export function useCancelQuery<
  TData = Awaited<ReturnType<typeof cancelQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelQueryPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelQuery>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelQuery>>,
          TError,
          Awaited<ReturnType<typeof cancelQuery>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelQuery<
  TData = Awaited<ReturnType<typeof cancelQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelQueryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelQuery>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelQuery>>,
          TError,
          Awaited<ReturnType<typeof cancelQuery>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelQuery<
  TData = Awaited<ReturnType<typeof cancelQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelQueryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel Query
 */

export function useCancelQuery<
  TData = Awaited<ReturnType<typeof cancelQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: CancelQueryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCancelQueryQueryOptions({ jobId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Cancel Query
 */
export const prefetchCancelQueryQuery = async <
  TData = Awaited<ReturnType<typeof cancelQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { jobId }: CancelQueryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCancelQueryQueryOptions({ jobId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Cancel Query
 */
export const invalidateCancelQuery = async (
  queryClient: QueryClient,
  { jobId }: CancelQueryPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCancelQueryQueryKey({ jobId }) }, options);

  return queryClient;
};

/**
 * @summary Get Query Result Data
 */
export const getQueryResultData = (
  { jobId }: GetQueryResultDataPathParameters,
  params?: GetQueryResultDataParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLQueryResultDataResponse>(
    { url: `/foundry/query/${jobId}/data`, method: 'GET', params, signal },
    options,
  );
};

export const getGetQueryResultDataQueryKey = (
  { jobId }: GetQueryResultDataPathParameters,
  params?: GetQueryResultDataParams,
) => {
  return ['foundry', 'query', jobId, 'data', ...(params ? [params] : [])] as const;
};

export const getGetQueryResultDataQueryOptions = <
  TData = Awaited<ReturnType<typeof getQueryResultData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetQueryResultDataPathParameters,
  params?: GetQueryResultDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryResultData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetQueryResultDataQueryKey({ jobId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getQueryResultData>>> = ({ signal }) =>
    getQueryResultData({ jobId }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getQueryResultData>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetQueryResultDataQueryResult = NonNullable<Awaited<ReturnType<typeof getQueryResultData>>>;
export type GetQueryResultDataQueryError = ErrorType<TLHTTPValidationError>;

export function useGetQueryResultData<
  TData = Awaited<ReturnType<typeof getQueryResultData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetQueryResultDataPathParameters,
  params: undefined | GetQueryResultDataParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryResultData>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getQueryResultData>>,
          TError,
          Awaited<ReturnType<typeof getQueryResultData>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetQueryResultData<
  TData = Awaited<ReturnType<typeof getQueryResultData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetQueryResultDataPathParameters,
  params?: GetQueryResultDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryResultData>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getQueryResultData>>,
          TError,
          Awaited<ReturnType<typeof getQueryResultData>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetQueryResultData<
  TData = Awaited<ReturnType<typeof getQueryResultData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetQueryResultDataPathParameters,
  params?: GetQueryResultDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryResultData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Query Result Data
 */

export function useGetQueryResultData<
  TData = Awaited<ReturnType<typeof getQueryResultData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: GetQueryResultDataPathParameters,
  params?: GetQueryResultDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryResultData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetQueryResultDataQueryOptions({ jobId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Query Result Data
 */
export const prefetchGetQueryResultDataQuery = async <
  TData = Awaited<ReturnType<typeof getQueryResultData>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { jobId }: GetQueryResultDataPathParameters,
  params?: GetQueryResultDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getQueryResultData>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetQueryResultDataQueryOptions({ jobId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Query Result Data
 */
export const invalidateGetQueryResultData = async (
  queryClient: QueryClient,
  { jobId }: GetQueryResultDataPathParameters,
  params?: GetQueryResultDataParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetQueryResultDataQueryKey({ jobId }, params) }, options);

  return queryClient;
};

/**
 * @summary Export Foundry Query Result
 */
export const exportFoundryQueryResult = (
  { jobId }: ExportFoundryQueryResultPathParameters,
  tLExportQueryResultRequest: BodyType<TLExportQueryResultRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFoundryExportStatusResponse>(
    {
      url: `/foundry/query/${jobId}/export`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLExportQueryResultRequest,
      signal,
    },
    options,
  );
};

export const getExportFoundryQueryResultQueryKey = (
  { jobId }: ExportFoundryQueryResultPathParameters,
  tLExportQueryResultRequest?: BodyType<TLExportQueryResultRequest>,
) => {
  return ['POST', 'foundry', 'query', jobId, 'export', tLExportQueryResultRequest] as const;
};

export const getExportFoundryQueryResultQueryOptions = <
  TData = Awaited<ReturnType<typeof exportFoundryQueryResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: ExportFoundryQueryResultPathParameters,
  tLExportQueryResultRequest: BodyType<TLExportQueryResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportFoundryQueryResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getExportFoundryQueryResultQueryKey({ jobId }, tLExportQueryResultRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof exportFoundryQueryResult>>> = ({ signal }) =>
    exportFoundryQueryResult({ jobId }, tLExportQueryResultRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof exportFoundryQueryResult>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ExportFoundryQueryResultQueryResult = NonNullable<Awaited<ReturnType<typeof exportFoundryQueryResult>>>;
export type ExportFoundryQueryResultQueryError = ErrorType<TLHTTPValidationError>;

export function useExportFoundryQueryResult<
  TData = Awaited<ReturnType<typeof exportFoundryQueryResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportFoundryQueryResultPathParameters,
  tLExportQueryResultRequest: BodyType<TLExportQueryResultRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportFoundryQueryResult>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportFoundryQueryResult>>,
          TError,
          Awaited<ReturnType<typeof exportFoundryQueryResult>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportFoundryQueryResult<
  TData = Awaited<ReturnType<typeof exportFoundryQueryResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportFoundryQueryResultPathParameters,
  tLExportQueryResultRequest: BodyType<TLExportQueryResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportFoundryQueryResult>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportFoundryQueryResult>>,
          TError,
          Awaited<ReturnType<typeof exportFoundryQueryResult>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportFoundryQueryResult<
  TData = Awaited<ReturnType<typeof exportFoundryQueryResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportFoundryQueryResultPathParameters,
  tLExportQueryResultRequest: BodyType<TLExportQueryResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportFoundryQueryResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Export Foundry Query Result
 */

export function useExportFoundryQueryResult<
  TData = Awaited<ReturnType<typeof exportFoundryQueryResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { jobId }: ExportFoundryQueryResultPathParameters,
  tLExportQueryResultRequest: BodyType<TLExportQueryResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportFoundryQueryResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExportFoundryQueryResultQueryOptions({ jobId }, tLExportQueryResultRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Export Foundry Query Result
 */
export const prefetchExportFoundryQueryResultQuery = async <
  TData = Awaited<ReturnType<typeof exportFoundryQueryResult>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { jobId }: ExportFoundryQueryResultPathParameters,
  tLExportQueryResultRequest: BodyType<TLExportQueryResultRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportFoundryQueryResult>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExportFoundryQueryResultQueryOptions({ jobId }, tLExportQueryResultRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Export Foundry Query Result
 */
export const invalidateExportFoundryQueryResult = async (
  queryClient: QueryClient,
  { jobId }: ExportFoundryQueryResultPathParameters,
  tLExportQueryResultRequest: BodyType<TLExportQueryResultRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getExportFoundryQueryResultQueryKey({ jobId }, tLExportQueryResultRequest) },
    options,
  );

  return queryClient;
};
